/**
 * Display/parse helpers for PDF form number fields.
 * Grouping and decimal symbols come from the runtime locale (browser/OS
 * regional settings). Stored values stay canonical: no grouping, '.' decimal.
 * Whole numbers have no fraction; other values keep as many decimal digits as entered.
 */

type LocaleNumberSymbols = { group: string; decimal: string };

const INTL_NUMBERING = { numberingSystem: 'latn' } as const;

let symbolsCache: LocaleNumberSymbols & { locale: string } | null = null;

export function getLocaleNumberSymbols(): LocaleNumberSymbols {
  const locale = Intl.NumberFormat().resolvedOptions().locale;
  if (symbolsCache && symbolsCache.locale === locale) {
    return { group: symbolsCache.group, decimal: symbolsCache.decimal };
  }
  const parts = new Intl.NumberFormat(undefined, {
    ...INTL_NUMBERING,
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(1234567.89);
  const group = parts.find(p => p.type === 'group')?.value ?? ',';
  const decimal = parts.find(p => p.type === 'decimal')?.value ?? '.';
  symbolsCache = { locale, group, decimal };
  return { group, decimal };
}

function stripGrouping(intRaw: string, group: string): string {
  let s = intRaw;
  const separators = new Set<string>([group, ' ', '\u00a0', '\u202f', '\u2009', "'", '’']);
  separators.forEach((sep) => {
    if (!sep) return;
    s = s.split(sep).join('');
  });
  return s;
}

function formatIntegerDigits(intPart: string): string {
  const digits = intPart.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > 15) {
    const { group } = getLocaleNumberSymbols();
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  }
  return Number(digits).toLocaleString(undefined, {
    ...INTL_NUMBERING,
    useGrouping: true,
    maximumFractionDigits: 0,
  });
}

function isNumericDraftChar(ch: string, decimal: string, group: string): boolean {
  if (ch === group) return false;
  return /\d/.test(ch) || ch === '-' || ch === '−' || ch === decimal;
}

function parseLocalizedNumberDraft(raw: string): {
  neg: boolean;
  intPart: string;
  frac: string;
  hadDecimal: boolean;
} {
  const { group, decimal } = getLocaleNumberSymbols();
  let s = String(raw ?? '').trim();
  const neg = s.startsWith('-') || s.startsWith('−');
  if (neg) s = s.slice(1);

  let dec = decimal;
  const hasLocaleDecimal = decimal ? s.includes(decimal) : false;
  if (!hasLocaleDecimal && decimal !== '.' && /\.\d+$/.test(s)) {
    dec = '.';
  }

  const hadDecimal = !!dec && s.includes(dec);
  let intRaw = s;
  let fracRaw = '';
  if (hadDecimal) {
    const idx = s.lastIndexOf(dec);
    intRaw = s.slice(0, idx);
    fracRaw = s.slice(idx + 1);
  }

  let intPart = stripGrouping(intRaw, group).replace(/\D/g, '');
  intPart = intPart.replace(/^0+(?=\d)/, '');
  const frac = fracRaw.replace(/\D/g, '');
  return { neg, intPart, frac, hadDecimal };
}

export function formatNumberTyping(raw: string): { display: string; canonical: string } {
  const trimmed = String(raw ?? '');
  if (trimmed === '' || trimmed === '-' || trimmed === '−') {
    const display = trimmed === '−' ? '-' : trimmed;
    return { display, canonical: display };
  }

  const { decimal } = getLocaleNumberSymbols();
  const { neg, intPart, frac, hadDecimal } = parseLocalizedNumberDraft(trimmed);

  if (!intPart && !hadDecimal) {
    return { display: neg ? '-' : '', canonical: neg ? '-' : '' };
  }

  const intForDisplay = intPart || '0';
  const sign = neg ? '-' : '';
  const canonical = `${sign}${intForDisplay}${hadDecimal ? `.${frac}` : ''}`;
  const display = `${sign}${formatIntegerDigits(intForDisplay)}${hadDecimal ? `${decimal}${frac}` : ''}`;
  return { display, canonical };
}

export function finalizeNumberCanonical(value: string): string {
  const { canonical } = formatNumberTyping(value);
  const s = canonical.trim();
  if (s === '' || s === '-' || s === '.' || s === '-.') return '';
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  const [intPart, frac = ''] = body.split('.');
  const intNorm = (intPart || '0').replace(/^0+(?=\d)/, '') || '0';
  const fracNorm = frac.replace(/0+$/, '');
  const sign = neg ? '-' : '';
  if (!fracNorm) return `${sign}${intNorm}`;
  return `${sign}${intNorm}.${fracNorm}`;
}

export function formatNumberDisplay(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  const s = finalizeNumberCanonical(String(value));
  if (s === '') return '';
  const { decimal } = getLocaleNumberSymbols();
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  const [intPart, frac] = body.split('.');
  const sign = neg ? '-' : '';
  const grouped = formatIntegerDigits(intPart || '0');
  if (!frac) return `${sign}${grouped}`;
  return `${sign}${grouped}${decimal}${frac}`;
}

export function caretIndexAfterNumericChars(formatted: string, numericCount: number): number {
  if (numericCount <= 0) return 0;
  const { decimal, group } = getLocaleNumberSymbols();
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (isNumericDraftChar(formatted[i], decimal, group)) {
      seen += 1;
      if (seen === numericCount) return i + 1;
    }
  }
  return formatted.length;
}

export function countNumericChars(s: string): number {
  const { decimal, group } = getLocaleNumberSymbols();
  let n = 0;
  for (const ch of s) {
    if (isNumericDraftChar(ch, decimal, group)) n += 1;
  }
  return n;
}

export function addThousandsCommas(intPart: string): string {
  return formatIntegerDigits(intPart);
}
